import { reactive } from 'vue'
import { getItemRef, saveItem } from '@/plugins/storage'
import markupRenderService from '@/services/markup-service/render'
import marketingEventService from '@/services/marketing-event-service'

// reactive state
const observableState = reactive({
  // hide
  slopeAngle: false,
  weatherCrosshair: false,

  // show
  nav: true,
  toolbox: true,
  search: true,
  compass: true,
  zoom: true,
  basemap: true,
  userLocation: true,
  activityMode: true,
  weather: true,
  legacyWeather: true,
  appSwitcher: true,
  huntLayers: true,
  terrainX: true,
  huntResearchToolsControl: true,
  dimensions: true,
  layers: true,
  mobileMenuOpen: false,

  // pref
  crosshair: getItemRef('crosshairsToggle'),
  scale: getItemRef('scalebarToggle'),
  cursorLocation: getItemRef('coordinateToggle'),
  // FUTURE -  Move to map-service once browserStorage has been refactored
  namesOnWaypoints: getItemRef('namesOnWaypoints'),
  waypointClustering: getItemRef('waypointClustering'),
})

/**
 * *(AI-generated)*
 * Returns the reactive control layout state object.
 */
export const controlState = () => observableState

// Setters
/**
 * *(AI-generated)*
 * Sets the visibility of the navigation control.
 */
export const setNav = (show) => {
  observableState.nav = show
}
/**
 * *(AI-generated)*
 * Sets the visibility of the search control.
 */
export const setSearch = (show) => {
  observableState.search = show
}
/**
 * *(AI-generated)*
 * Sets the visibility of the toolbox control.
 */
export const setToolbox = (show) => {
  observableState.toolbox = show
}

/**
 * *(AI-generated)*
 * Sets the visibility of the TerrainX control.
 */
export const setTerrainX = (show) => {
  observableState.terrainX = show
}

/**
 * *(AI-generated)*
 * Sets the visibility of the hunt research tools control.
 */
export const setHuntResearchTool = (show) => {
  observableState.huntResearchToolsControl = show
}

/**
 * Function for explictily setting the slope angle state to show the opacity slider
 *
 * @param {boolean} enabled
 */
export const setEnableSlopeAngle = (enabled) => {
  observableState.slopeAngle = enabled
}

/**
 * *(AI-generated)*
 * Sets the visibility of the cursor coordinate display and persists the preference.
 */
export const setCoordinate = (show) => {
  observableState.cursorLocation = show
  saveItem('coordinateToggle', show)
  marketingEventService.publish('toggled_coordinates_web', {
    show_coordinates: show,
  })
}

/**
 * *(AI-generated)*
 * Sets whether waypoint names are shown on the map and persists the preference.
 */
export const setNamesOnWaypoints = (show) => {
  observableState.namesOnWaypoints = show
  saveItem('namesOnWaypoints', show)
  markupRenderService.update(true)
  marketingEventService.publish('settings_waypoint_labels_toggled', {
    setting: show ? 'enabled' : 'disabled',
  })
}

/**
 * *(AI-generated)*
 * Sets whether waypoints are clustered on the map and persists the preference.
 */
export const setWaypointClustering = (show) => {
  observableState.waypointClustering = show
  saveItem('waypointClustering', show)
  markupRenderService.update(true)
  marketingEventService.publish('settings_waypoint_clustering_toggled', {
    setting: show ? 'enabled' : 'disabled',
  })
}

/**
 * *(AI-generated)*
 * Sets the visibility of the scale bar and persists the preference.
 */
export const setScale = (show) => {
  observableState.scale = show
  saveItem('scalebarToggle', show)
  marketingEventService.publish('toggled_scalebar_web', { scalebar: show })
}

/**
 * *(AI-generated)*
 * Sets the visibility of the crosshair and optionally persists the preference.
 */
export const setCrosshair = (show, userAction = true) => {
  observableState.crosshair = show

  if (userAction) {
    saveItem('crosshairsToggle', show)
    marketingEventService.publish('toggled_crosshairs_web', { crosshair: show })
  }
}

/**
 * *(AI-generated)*
 * Sets the visibility of the weather crosshair.
 */
export const setWeatherCrosshair = (show) => {
  observableState.weatherCrosshair = show
}

/**
 * *(AI-generated)*
 * Sets the visibility of the weather controls.
 */
export const setWeather = (show) => {
  observableState.weather = show
  observableState.legacyWeather = show
}

/**
 * *(AI-generated)*
 * Sets whether the mobile menu is open.
 */
export const setMobileMenuOpen = (show) => {
  observableState.mobileMenuOpen = show
}

/**
 * Show/hide hunt tools in top right corner
 * @param {boolean} show - Whether to show or not the top right tools
 */
export const setShowTopRightHuntTools = (show) => {
  setToolbox(show)
  setTerrainX(show)
  setHuntResearchTool(show)
}

// Disable tools but do not hide
/**
 * *(AI-generated)*
 * Disables or re-enables the activity mode control without hiding it.
 */
export const disableActivityMode = (disable) => {
  observableState.activityMode = disable ? 'disabled' : true
}

/**
 * *(AI-generated)*
 * Disables or re-enables the app switcher control without hiding it.
 */
export const disableAppSwitcher = (disable) => {
  observableState.appSwitcher = disable ? 'disabled' : true
}

/**
 * *(AI-generated)*
 * Disables or re-enables the weather control without hiding it.
 */
export const disableWeather = (disable) => {
  observableState.weather = disable ? 'disabled' : true
}

/**
 * *(AI-generated)*
 * Disables or re-enables the compass control without hiding it.
 */
export const disableCompass = (disable) => {
  observableState.compass = disable ? 'disabled' : true
}

/**
 * *(AI-generated)*
 * Disables or re-enables the basemap control without hiding it.
 */
export const disableBasemap = (disable) => {
  observableState.basemap = disable ? 'disabled' : true
}

/**
 * *(AI-generated)*
 * Disables or re-enables the zoom control without hiding it.
 */
export const disableZoom = (disable) => {
  observableState.zoom = disable ? 'disabled' : true
}

/**
 * *(AI-generated)*
 * Disables or re-enables the dimensions control without hiding it.
 */
export const disableDimensions = (disable) => {
  observableState.dimensions = disable ? 'disabled' : true
}

/**
 * *(AI-generated)*
 * Disables or re-enables the layers control without hiding it.
 */
export const disableLayers = (disable) => {
  observableState.layers = disable ? 'disabled' : true
}

/**
 * *(AI-generated)*
 * Disables or re-enables the TerrainX control without hiding it.
 */
export const disableTerrainX = (disable) => {
  observableState.terrainX = disable ? 'disabled' : true
}
